import React from 'react';
import { motion } from 'framer-motion';
// import InteractiveMap from '../components/InteractiveMap'; // Removed
import WhatsAppConnect from '../components/whatsappConnect';
// import InquiryButton from '../components/InquiryButton'; // Removed

// Navbar component
const PageNavbar = () => (
  <nav className="fixed top-0 w-full bg-white shadow-md z-50 flex items-center justify-between px-6 h-24 overflow-hidden">
    <img src="/Hydrocawach_logo.png" alt="HydroCawach Logo" className="h-36 w-auto object-contain" />
    <h1 className="absolute left-1/2 transform -translate-x-1/2 text-blue-600 text-lg md:text-2xl font-bold text-center">
      Edible Oil Refining
    </h1>
  </nav>
);

// --- DATA FOR KEY PROCESSES ---
const processEnhancements = [
  {
    title: 'Degumming',
    description: 'Breaks phospholipid bonds and improves gum separation efficiency.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5.16 14.75H3.372v-2.1l-.38-3.8A6.75 6.75 0 019.75 3.104zM20.628 14.75h-2.148L14.64 10.409a2.25 2.25 0 01-.659-1.591v-5.714A6.75 6.75 0 0121 8.85l-.38 3.8v2.1z" />
      </svg>
    )
  },
  {
    title: 'Neutralization',
    description: 'Accelerates reaction of free fatty acids with alkali, reducing soap formation.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-9.036 0c-.483-.174-.711-.703-.59-1.202L18.75 4.971z" />
      </svg>
    )
  },
  {
    title: 'Bleaching',
    description: 'Enhances contact between oil and adsorbent, improving color and oxidation stability.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-6.364-.386l1.591-1.591M3 12H5.25m.386-6.364l1.591 1.591M12 6a3 3 0 100 6 3 3 0 000-6z" />
      </svg>
    )
  }
];

// --- DATA FOR ADVANTAGES ---
const advantages = [
  {
    icon: '⚡',
    title: 'Faster Processing',
    description: '3–5× quicker than conventional systems.'
  },
  {
    icon: '🌱',
    title: 'Low Chemical & Energy Use',
    description: 'Reduced caustic, bleaching earth, and heating.'
  },
  {
    icon: '💧',
    title: 'Improved Yield & Purity',
    description: 'Lower oil losses during refining.'
  },
  {
    icon: '🔄',
    title: 'Compact & Continuous',
    description: 'Ideal for retrofitting existing refineries.'
  },
  {
    icon: '♻️',
    title: 'Sustainable Technology',
    description: 'Green, scalable, and cost-effective solution.'
  }
];

// --- DATA FOR APPLICATIONS ---
const applications = [
  {
    title: 'Refining of Edible Oils',
    description: 'Soybean, sunflower, mustard, palm, groundnut, and rice bran oils.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048l5.962-5.962a.75.75 0 011.06 0l2.3 2.3a.75.75 0 010 1.06l-2.3 2.3z" />
      </svg>
    )
  },
  {
    title: 'Specialty Fats & Biodiesel Feedstocks',
    description: 'Enhanced impurity removal and conversion efficiency.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.345A6.34 6.34 0 005.655 12 6.34 6.34 0 0012 17.655 6.34 6.34 0 0018.345 12 6.34 6.34 0 0012 6.345z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3.105a9.105 9.105 0 00-9.105 9.105 9.105 9.105 0 009.105 9.105 9.105 9.105 0 009.105-9.105A9.105 9.105 0 0012 3.105z" />
      </svg>
    )
  }
];

const EdibleOilRefining = () => (
  <div className="bg-white text-gray-800 min-h-screen relative overflow-x-hidden">
    <PageNavbar />

    <div className="pt-32 px-4"> {/* Increased top padding */}
      <div className="p-8 max-w-6xl mx-auto">

        {/* Introduction Paragraphs */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-lg text-center text-gray-700 max-w-4xl mx-auto mb-8"
        >
          Cavitation technology transforms the refining of edible oils by enabling efficient degumming,
          neutralization, and bleaching through intense micro-mixing and localized
          energy release. In hydrodynamic cavitation, vapor bubbles form and
          collapse within the oil-water mixture, producing high shear, pressure, and
          turbulence — driving rapid mass transfer and reaction enhancement.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-xl font-medium text-center text-blue-700 max-w-4xl mx-auto mb-16"
        >
          This innovation ensures superior impurity
          removal, reduced chemical consumption, and enhanced oil clarity,
          all while minimizing processing time and energy use.
        </motion.p>

        {/* Key Process Enhancements Section */}
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Key Process Enhancements</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {processEnhancements.map((process, index) => (
            <motion.div
              key={process.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white border border-gray-200 rounded-lg shadow-lg p-6 flex items-start space-x-4 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full text-blue-600">
                {process.icon}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{process.title}</h3>
                <p className="text-gray-700">{process.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Advantages Section */}
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Advantages</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-20">
          {advantages.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white border border-gray-200 rounded-lg shadow-lg p-6 flex flex-col items-center text-center hover:shadow-xl transition-shadow duration-300"
            >
              <div className="text-5xl mb-4">{benefit.icon}</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
              <p className="text-gray-700">{benefit.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Applications Section */}
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Applications</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {applications.map((app, index) => (
            <motion.div
              key={app.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg shadow-md p-6 flex items-start space-x-4"
            >
              <div className="flex-shrink-0 text-yellow-600">{app.icon}</div>
              <div>
                <h3 className="text-xl font-bold text-yellow-800 mb-2">{app.title}</h3>
                <p className="text-gray-700">{app.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </div>

    <WhatsAppConnect />
  </div>
);

export default EdibleOilRefining;